// Frontend input validation utilities
// Provides better UX with real-time validation

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Validate email with improved regex
export const validateEmail = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();

  // More robust email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  if (trimmedEmail.length > 320) {
    return { isValid: false, error: 'Email address is too long' };
  }

  // Check for common typos
  const commonTypos = [
    { wrong: '@gmial.com', correct: '@gmail.com' },
    { wrong: '@gmai.com', correct: '@gmail.com' },
    { wrong: '@yahooo.com', correct: '@yahoo.com' },
    { wrong: '@hotmial.com', correct: '@hotmail.com' },
  ];

  for (const typo of commonTypos) {
    if (trimmedEmail.endsWith(typo.wrong)) {
      return { 
        isValid: false, 
        error: `Did you mean ${trimmedEmail.replace(typo.wrong, typo.correct)}?` 
      };
    }
  }

  return { isValid: true };
};

// Validate Australian phone number
export const validatePhone = (phone: string): ValidationResult => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Remove all non-digits
  const digitsOnly = phone.replace(/\D/g, '');

  // Australian mobile: 04XX XXX XXX (10 digits)
  // Australian landline: (0X) XXXX XXXX (10 digits with area code)
  if (digitsOnly.length < 8) {
    return { isValid: false, error: 'Phone number is too short' };
  }

  if (digitsOnly.length > 15) {
    return { isValid: false, error: 'Phone number is too long' };
  }

  // Check if it's a valid Australian mobile (starts with 04)
  if (digitsOnly.length === 10 && !digitsOnly.startsWith('04')) {
    return { 
      isValid: false, 
      error: 'Please enter a valid Australian phone number (mobile: 04XX XXX XXX)' 
    };
  }

  return { isValid: true };
};

// Format phone number as user types
export const formatPhoneNumber = (phone: string): string => {
  const digitsOnly = phone.replace(/\D/g, '');

  // Format as 04XX XXX XXX for mobiles
  if (digitsOnly.startsWith('04') && digitsOnly.length <= 10) {
    if (digitsOnly.length <= 4) return digitsOnly;
    if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4)}`;
    return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 10)}`;
  }

  return phone;
};

// Validate Australian postcode
export const validatePostcode = (postcode: string): ValidationResult => {
  if (!postcode || !postcode.trim()) {
    return { isValid: false, error: 'Postcode is required' };
  }

  const trimmed = postcode.trim();

  if (!/^\d{4}$/.test(trimmed)) {
    return { isValid: false, error: 'Australian postcode must be 4 digits' };
  }

  // Basic range check (Australian postcodes are 0200-9999)
  const code = parseInt(trimmed, 10);
  if (code < 200 || code > 9999) {
    return { isValid: false, error: 'Invalid Australian postcode' };
  }

  return { isValid: true };
};

// Validate name
export const validateName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Name is required' };
  }

  const trimmed = name.trim();

  if (trimmed.length < 2) {
    return { isValid: false, error: 'Name is too short' };
  }

  if (trimmed.length > 100) {
    return { isValid: false, error: 'Name is too long' };
  }

  // Check for at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, error: 'Name must contain letters' };
  }

  return { isValid: true };
};

// Validate address
export const validateAddress = (address: string): ValidationResult => {
  if (!address || !address.trim()) {
    return { isValid: false, error: 'Address is required' };
  }

  const trimmed = address.trim();

  if (trimmed.length < 3) {
    return { isValid: false, error: 'Address is too short' };
  }

  if (trimmed.length > 100) {
    return { isValid: false, error: 'Address is too long' };
  }

  return { isValid: true };
};

// Validate city
export const validateCity = (city: string): ValidationResult => {
  if (!city || !city.trim()) {
    return { isValid: false, error: 'City is required' };
  }

  const trimmed = city.trim();

  if (trimmed.length < 2) {
    return { isValid: false, error: 'City name is too short' };
  }

  if (trimmed.length > 50) {
    return { isValid: false, error: 'City name is too long' };
  }

  return { isValid: true };
};

// Sanitize input (prevent XSS)
export const sanitizeInput = (input: string): string => {
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .trim()
    .substring(0, 255); // Limit length
};

// Validate all shipping form fields
export const validateShippingForm = (address: {
  fullName: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  postcode: string;
}): Record<string, string> => {
  const errors: Record<string, string> = {};

  const nameValidation = validateName(address.fullName);
  if (!nameValidation.isValid) {
    errors.fullName = nameValidation.error!;
  }

  const emailValidation = validateEmail(address.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error!;
  }

  const phoneValidation = validatePhone(address.phone);
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.error!;
  }

  const addressValidation = validateAddress(address.addressLine1);
  if (!addressValidation.isValid) {
    errors.addressLine1 = addressValidation.error!;
  }

  const cityValidation = validateCity(address.city);
  if (!cityValidation.isValid) {
    errors.city = cityValidation.error!;
  }

  const postcodeValidation = validatePostcode(address.postcode);
  if (!postcodeValidation.isValid) {
    errors.postcode = postcodeValidation.error!;
  }

  return errors;
};